import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FormContainer } from 'react-hook-form-mui'
import { notification } from '../../components/Notifications'
import { Box, Chip, Container, Typography } from '@mui/material'
import GenericAutocomplete from '../../components/FormElements/FormAutoComplete'
import SinglePageView from './SinglePageView'
import SectionBySectionView from './SectionBySectionView'
import PageHeader from '../../components/PageHeader'
import ExpandableText from '../../components/ExpandableText'
import { useQuery } from '@tanstack/react-query'
import LoadingPageV2 from '../../pages/LoadingPageV2'
import NotFoundCard from '../../components/NotFoundCard'
import { AutoImportManager } from '../../APIManager'
import { blue } from '../../colors'

export enum PreviewTypes {
  SINGLE_PAGE_VIEW = 'Single-page View',
  STUDENT_EXPERIENCE = 'Student Experience',
}

export const previewTypesDropdown = [PreviewTypes.SINGLE_PAGE_VIEW, PreviewTypes.STUDENT_EXPERIENCE]

interface ISectionAttribute {
  value?: string
}

const SurveyPreviewFeature: React.FC = () => {
  const [previewType, setPreviewType] = useState<PreviewTypes>(PreviewTypes.SINGLE_PAGE_VIEW)

  const { slotId, sectionId } = useParams()

  const { data: survey, isLoading, isError } = useQuery({
    queryKey: ['dates', slotId, sectionId],
    queryFn: async () => await AutoImportManager.surveyPreview(slotId ?? '', sectionId ?? ''),
    enabled: !!slotId && !!sectionId,
    staleTime: 10000,
    retry: false
  })

  const handlePreviewTypeChange = (value: PreviewTypes | null): void => {
    setPreviewType(value as PreviewTypes)
  }

  const instructorNames = survey?.instructors?.map((instructor: { title: string }) => instructor.title).join(', ')
  const teachingAssistantNames = survey?.teachingAssistants?.map((ta: { title: string }) => ta.title).join(', ')

  const onSubmit = async (): Promise<void> => {
    notification('Survey Preview Completed', { variant: 'success' })
  }

  const headerAttributeValues = useMemo(() => {
    const headerAttributes: ISectionAttribute[] = survey?.section?.attributes ?? []

    return Array.from(new Set(
      headerAttributes
        .map((attribute) => attribute?.value?.trim() ?? '')
        .filter((value) => value.length > 0)
    ))
  }, [survey?.section?.attributes])

  // show instructor name in subheading
  const sectionAbbr = survey?.section?.abbreviation
  const instructorLine = [sectionAbbr, instructorNames].filter(Boolean).join(' | ')
  // Large team-taught sections wrap the instructor line over many rows, so it collapses
  // to its first row behind a Show more toggle. TAs (CE-158) render name-only on their
  // own row beneath it and stay unclamped.
  const description = (instructorLine || teachingAssistantNames)
    ? (
      <Box sx={{ mb: 3 }}>
        <ExpandableText>{instructorLine}</ExpandableText>
        {teachingAssistantNames && <Typography variant="body1">TAs: {teachingAssistantNames}</Typography>}
      </Box>
      )
    : undefined

  if (isLoading) {
    return <LoadingPageV2 />
  }

  if (isError) {
    return <NotFoundCard />
  }

  const previewTypeControl = <Box>
    <GenericAutocomplete
      defaultValue={previewTypesDropdown[0]}
      name="surveyPreviewType"
      getOptionLabel={(option: string) => option}
      getOptionValue={(option: string) => option}
      options={previewTypesDropdown}
      onChange={handlePreviewTypeChange}
      sx={{ width: '270px' }}
      wrapperStyle={{ minHeight: 'auto' }}
    />
  </Box>

  const showSurvey = (): JSX.Element => {
    switch (previewType) {
      case PreviewTypes.SINGLE_PAGE_VIEW:
        return <SinglePageView surveySections={survey?.surveySections} />
      case PreviewTypes.STUDENT_EXPERIENCE:
        return <SectionBySectionView initialFetchedSurveys={survey} onSubmit={onSubmit} />
      default:
        return <SectionBySectionView initialFetchedSurveys={survey} onSubmit={onSubmit} />
    }
  }

  return (
    <FormContainer>
      <PageHeader
        title={survey?.title || ''}
        attributes={
          <Box sx={{ mt: 1 }}>
            {headerAttributeValues.map((value, index) =>
              <Chip
                key={index}
                label={value}
                variant="filled"
                color="info"
                size="small"
                sx={{
                  borderRadius: '6px',
                  border: `1px solid ${blue[150]}`,
                  fontWeight: 500,
                  color: blue[450],
                  backgroundColor: blue[0],
                  marginRight: 1
                }}
              />
            )}
          </Box>
        }
        description={description}
        isCompactOnly
        controls={previewTypeControl}
      />

      <Box width="100%">
        <Container>
          {showSurvey()}
        </Container>
      </Box>
    </FormContainer>
  )
}

export default SurveyPreviewFeature
